import { Component } from '@angular/core'
import { FormGroup, FormControl } from '@angular/forms'
import {
  SharedModule,
  CustomDetailComponent,
  ProductVariant,
  SearchResult,
  CurrencyCode,
  DataService,
  NotificationService,
} from '@vendure/admin-ui/core'
import { Observable, forkJoin } from 'rxjs'
import { ID } from '@vendure/core'
import { ChangeDetectorRef } from '@angular/core'
import { gql } from 'apollo-angular'

const UPDATE_BUNDLE_ITEMS = gql`
  mutation updateBundleItems($bundleId: ID!, $items: [BundleItemInput!]!) {
    updateBundleItems(bundleId: $bundleId, items: $items)
  }
`

const GET_BUNDLED_VARIANTS = gql`
  query GetBundledVariants($id: ID!) {
    productVariant(id: $id) {
      id
      bundledVariants {
        id
        price
        quantity
        variant {
          name
          id
          sku
          productId
        }
      }
    }
  }
`

const UPDATE_PRODUCT_VARIANT = gql`
  mutation UpdateProductVariant($input: UpdateProductVariantInput!) {
    updateProductVariant(input: $input) {
      id
      customFields {
        bundleListPrice
      }
    }
  }
`

interface selectedVariant {
  id: ID
  name: string
  price: number
  quantity: number
  sku?: string
  productId?: ID
}

@Component({
  selector: 'select-bundled-variant-widget',
  templateUrl: './select-variant.component.html',
  standalone: true,
  imports: [SharedModule],
})
export class SelectVariantComponent implements CustomDetailComponent {
  entity$!: Observable<ProductVariant>
  detailForm!: FormGroup
  currencyCode: CurrencyCode
  selectedVariants: selectedVariant[] = []
  bundleListPriceControl!: FormControl

  constructor(
    private cdRef: ChangeDetectorRef,
    private dataService: DataService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.bundleListPriceControl = new FormControl(null)

    this.entity$.subscribe((entity) => {
      const variantId = entity?.id
      this.currencyCode = entity?.currencyCode
      
      this.bundleListPriceControl.setValue(entity?.customFields?.bundleListPrice ?? null)

      if (!variantId) {
        return
      }

      this.dataService
        .query(GET_BUNDLED_VARIANTS, { id: variantId })
        .mapSingle((data: any) => data.productVariant.bundledVariants)
        .subscribe((bundledVariants: any[]) => {
          this.selectedVariants = bundledVariants.map((v: any) => ({
            id: v.variant.id,
            name: v.variant.name,
            price: v.price ?? 0,
            quantity: v.quantity ?? 1,
            sku: v.variant.sku,
            productId: v.variant.productId,
          }))
          this.cdRef.detectChanges()
        })
    })
  }

  onVariantSelected(variant: SearchResult) {
    const exists = this.selectedVariants.some((item) => item.id === variant.productVariantId)

    if (exists) {
      this.notificationService.warning('Variant already selected')
      return
    }

    this.selectedVariants.push({
      id: variant.productVariantId,
      name: variant.productVariantName,
      price: 0,
      sku: variant.sku,
      productId: variant.productId,
      quantity: 1,
    })
  }

  removeVariant(variant: selectedVariant) {
    this.selectedVariants = this.selectedVariants.filter((item) => item.id !== variant.id)
  }


  async saveBundle() {
    const bundleId = this.detailForm.get('id')?.value
    const bundleListPrice = this.bundleListPriceControl?.value ?? null

    const mutations = [
      this.dataService.mutate(UPDATE_BUNDLE_ITEMS, {
        bundleId: bundleId,
        items: this.selectedVariants.map((v) => ({
          variantId: v.id,
          price: v.price,
          quantity: v.quantity,
        })),
      }),
      this.dataService.mutate(UPDATE_PRODUCT_VARIANT, {
        input: {
          id: bundleId,
          customFields: {
            bundleListPrice: bundleListPrice,
          },
        },
      }),
    ]

    forkJoin(mutations).subscribe({
      next: () => {
        this.cdRef.detectChanges()
        this.notificationService.success('Bundle updated successfully')
      },
      error: (error) => {
        console.error('Could not save bundle:', error)
        this.notificationService.error(`Error saving bundle: ${error.message}`)
      },
    })
  }
}
